import equal from 'fast-deep-equal';
import {LocationDescriptorObject} from 'history';
import * as queryString from 'querystring';
import React from 'react';
import {Route, RouteComponentProps, Switch, useParams, withRouter, useLocation} from 'react-router-dom';
import {HostPageMessenger} from './lib/HostPageMessenger';
import {showToasts} from './lib/toaster';
import {AppDetailPage} from './pages/AppDetailPage';
import {AppsListPage} from './pages/AppsListPage';
import {SegmentAnalytics} from './lib/SegmentAnalytics';
import {directoryStore} from './stores/DirectoryStore';
import {reaction, when} from 'mobx';
import { DataSyncListPage } from './pages/DataSync/DataSyncListPage';
import CreateDataSyncPage from './pages/DataSync/CreateDataSyncPage';
import EditDataSyncPage from './pages/DataSync/EditDataSyncPage';
import { DataSyncExecutionsPage } from './pages/DataSync/DataSyncExecutionsPage';
import { DataSyncAnalyticsPage } from './pages/DataSync/DataSyncAnalyticsPage';
import { canonicalAppId } from './lib/canonicalAppId';
import styles from './Directory.module.sass';

interface DirectoryState {
  prLane: string | null;
}

class Directory extends React.Component<RouteComponentProps, DirectoryState> {
  public state: DirectoryState = {
    prLane: null,
  };

  private disposer: any;

  public render() {
    return (
      <>
        <Switch>
          <Route path="/app/:appId/:version">
            <AppDetailPageRenderer />
          </Route>
          <Route path="/app/:appId">
            <AppDetailPageRenderer />
          </Route>
          <Route exact path="/">
            <AppsListPage />
          </Route>
          <Route exact path="/data_syncs">
            <DataSyncListPage />
          </Route>
          <Route exact path="/data_syncs/new">
            <CreateDataSyncPage />
          </Route>
          <Route exact path="/data_syncs/:dataSyncId">
            <EditDataSyncPage />
          </Route>
          <Route exact path="/data_syncs/:dataSyncId/executions">
            <DataSyncExecutionsPage />
          </Route>
          <Route exact path="/data_syncs/:dataSyncId/analytics">
            <DataSyncAnalyticsPage />
          </Route>
          <Route path="/">
            <div>404</div>
          </Route>
        </Switch>
        {this.state.prLane && (
          <div className={styles.prLaneBadge}>
            PR Lane: {this.state.prLane}
          </div>
        )}
      </>
    );
  }

  public async componentDidUpdate(prevProps: Readonly<RouteComponentProps>, prevState: Readonly<{}>, snapshot?: any) {
    const {pathname, search, hash} = this.props.location;
    if (prevProps && prevProps.location && !equal(this.props.location, prevProps.location)) {
      HostPageMessenger.send('navigate', {pathname, search, hash});
    }
    const params = queryString.parse(search.startsWith('?') ? search.substring(1) : search);
    if (params.toasts) {
      showToasts(JSON.parse(atob(params.toasts as string)));
      delete params.toasts;
      this.props.history.replace({...this.props.location, search: queryString.encode(params)});
    }

    if (directoryStore.trackerId) {
      await this.trackAnalytics(params, pathname + search);
    }
  }

  public async componentDidMount() {
    if (import.meta.env.VITE_PR_LANE_ENABLED) {
      const cookieMatch = document.cookie.match(/(?:^|;\s*)ocp-pr-lane=([^;]+)/);
      const lane = cookieMatch?.[1]
        || new URLSearchParams(window.location.search).get('ocp-pr-lane');
      if (lane) {
        this.setState({ prLane: lane });
        const originalPush = this.props.history.push.bind(this.props.history);
        const originalReplace = this.props.history.replace.bind(this.props.history);
        const preserveLane = (location: any): any => {
          if (typeof location === 'string') {
            const url = new URL(location, window.location.origin);
            if (!url.searchParams.has('ocp-pr-lane')) {
              url.searchParams.set('ocp-pr-lane', lane);
            }
            return url.pathname + url.search + url.hash;
          }
          if (typeof location === 'object') {
            const params = new URLSearchParams(location.search || '');
            if (!params.has('ocp-pr-lane')) {
              params.set('ocp-pr-lane', lane);
            }
            return { ...location, search: '?' + params.toString() };
          }
          return location;
        };
        this.props.history.push = ((location: any, state?: any) => originalPush(preserveLane(location), state)) as any;
        this.props.history.replace = ((location: any, state?: any) =>
          originalReplace(preserveLane(location), state)) as any;
      }
    }

    HostPageMessenger.on('navigate', (location: LocationDescriptorObject) => {
      this.props.history.replace(location);
    });
    HostPageMessenger.send('readyToNavigate');

    if (directoryStore.prismContext && directoryStore.trackerId) {
      await SegmentAnalytics.identify();
    } else {
      this.disposer = reaction(() => [directoryStore.prismContext, directoryStore.trackerId], () => {
        if (directoryStore.prismContext && directoryStore.trackerId) {
          SegmentAnalytics.identify();
        }
      });
    }
  }

  public componentWillUnmount() {
    // Cleanup the reaction to prevent memory leaks
    if (this.disposer) {
      this.disposer();
    }
  }

  private async trackAnalytics(params: queryString.ParsedUrlQuery, path: string) {
    // App id from the path (undefined off /app routes). Version isn't in the URL, so it
    // comes from the detail store, guarded by app id to avoid reporting a stale version.
    const appId = canonicalAppId(path.match(/^\/app\/([^/?#]+)/)?.[1]);
    const matchesActiveApp = !!appId && directoryStore.activeAppId === appId;
    const appVersion = matchesActiveApp ? directoryStore.activeAppVersion : undefined;
    const isInstalled = matchesActiveApp ? directoryStore.activeAppInstalled : undefined;
    if (params?.section && params?.tab) {
      await SegmentAnalytics.track(
        'App Section', {page: params.tab, section: params.section, path, appId, appVersion, isInstalled}
      );
    } else if (params?.tab) {
      await SegmentAnalytics.track('App', {page: params.tab, path, appId, appVersion, isInstalled});
    } else {
      let page: string;

      if (path === '/') {
        page = 'All Apps';
      } else if (path.startsWith('/app/')) {
        page = 'App Detail';
      } else if (path.startsWith('/data_syncs')) {
        page = 'Data Syncs';
      } else {
        page = path;
      }
      // App Detail fires on navigation, before the store resolves the version. Wait for it
      // so the page view can include appVersion.
      const resolvedAppVersion = (page === 'App Detail' && appId && !appVersion)
        ? await waitForActiveAppVersion(appId)
        : appVersion;
      // Install state is published alongside the version, so it is populated once the wait resolves.
      const resolvedIsInstalled = (appId && directoryStore.activeAppId === appId)
        ? directoryStore.activeAppInstalled
        : isInstalled;
      await SegmentAnalytics.page(
        page, {path, appId, appVersion: resolvedAppVersion, isInstalled: resolvedIsInstalled}
      );
    }
  }
}

// Wait for the detail store to publish this app's version (capped by timeoutMs). The
// app-id guard prevents a later navigation from attaching a different app's version.
function waitForActiveAppVersion(appId: string, timeoutMs = 10000): Promise<string | undefined> {
  if (directoryStore.activeAppId === appId && directoryStore.activeAppVersion) {
    return Promise.resolve(directoryStore.activeAppVersion);
  }
  return new Promise((resolve) => {
    // eslint-disable-next-line prefer-const -- referenced by the `when` disposer below before assignment
    let timer: any;
    const disposer = when(
      () => directoryStore.activeAppId === appId && !!directoryStore.activeAppVersion,
      () => {
        clearTimeout(timer);
        resolve(directoryStore.activeAppVersion);
      }
    );
    timer = setTimeout(() => {
      disposer();
      resolve(directoryStore.activeAppId === appId ? directoryStore.activeAppVersion : undefined);
    }, timeoutMs);
  });
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function AppDetailPageRenderer() {
  const {appId, version} = useParams<{ appId: string, version: string }>();
  const standalone = useQuery().has('standalone');
  return <AppDetailPage appId={canonicalAppId(appId)!} version={version} hideBreadcrumbs={standalone} />;
}

export const DirectoryWithRouter = withRouter(Directory);
